import { Fragment } from "react";

import { PlusIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import useModalState from "@logics/hooks/useModalState";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { useFetchProjectCommentListQuery } from "@domains/project/network/projectQueries";
import { useCreateCommentMutation } from "@domains/project/network/projectMutations";
import { COMMENT_MESSAGE } from "@domains/project/constants/toastMessages";

import CommentBlocker from "../../components/CommentBlocker/CommentBlocker";
import NoCommentPlaceholder from "../../components/NoCommentPlaceholder/NoCommentPlaceholder";
import ReplyList from "../../components/ReplyList/ReplyList";
import CommentForm from "../../components/CommentForm/CommentForm";
import CommentCard from "../../components/CommentCard/CommentCard";

import * as S from "./CommentSection.style";

interface CommentSectionProps {
  projectId: string;
  hasJoined?: boolean;
}

const CommentSection = ({ projectId, hasJoined }: CommentSectionProps) => {
  const { user } = useAuthSession();
  const { openModal } = useModalState();

  const { data: commentsData } = useFetchProjectCommentListQuery(projectId);

  const commentCreateMutation = useCreateCommentMutation();

  const handleOpenCommentForm = () => {
    if (!hasJoined) {
      ToastError(COMMENT_MESSAGE.UNJOINED_USER);
      return;
    }

    const handleSubmitComment = async (content: string) => {
      if (!projectId) return;

      try {
        await commentCreateMutation.mutateAsync({
          projectId,
          content,
        });

        ToastSuccess(COMMENT_MESSAGE.COMMENT_ADDED);
      } catch (error) {
        ToastError(ERROR_MESSAGE.SERVER_ERROR);
      }
    };

    openModal({
      CustomComponent: (
        <CommentForm
          headerTitle="Add comment"
          onSubmitComment={handleSubmitComment}
        />
      ),
    });
  };

  return (
    <S.Container $isSelectable={hasJoined}>
      <S.CommentListContainer>
        {commentsData?.hasComment ? (
          <S.ReplyListContainer>
            {commentsData.commentList.map((comment) => (
              <Fragment key={comment.id}>
                <CommentCard
                  isMySelf={comment.authorId === user?.id}
                  projectId={projectId}
                  commentId={comment.id}
                  authorName={comment.authorName}
                  authorId={comment.authorId}
                  authorProfile={comment.profileImage}
                  commentPayload={comment.content}
                  commentedAt={comment.createdAt}
                />
                <ReplyList
                  replyList={comment.replyList || []}
                  currentUserId={user?.id as number}
                  projectId={projectId}
                  commentId={comment.id}
                />
              </Fragment>
            ))}
          </S.ReplyListContainer>
        ) : (
          <NoCommentPlaceholder />
        )}

        <S.AddCommentButton onClick={handleOpenCommentForm}>
          <PlusIcon strokeColor={grayScale.white} />
          <span>Add Comments</span>
        </S.AddCommentButton>
      </S.CommentListContainer>

      {commentsData?.hasComment && !hasJoined && <CommentBlocker />}
    </S.Container>
  );
};

export default CommentSection;
